import { prefersReducedMotion } from '@theme/utilities';

/**
 * Shared overflow-x carousel for Bo+Tee homepage-style rows.
 *
 * Use this for peek / progress-bar / hover-chevron carousels (category,
 * featured collection, shop-the-look, and any new section like them).
 *
 * Required DOM:
 * - A horizontally scrollable track (`overflow-x: auto`, typically scroll-snap)
 * - Optional CSS custom property on the host for the thumb-width progress fill
 * - Optional prev/next buttons (hidden at the ends)
 *
 * Features (`progress`, `nav`, `drag`) can be toggled per section:
 * - JS: `features: { progress: true, nav: false, drag: true }`
 * - Host attrs win when set: `data-bt-carousel-progress`, `data-bt-carousel-nav`,
 *   `data-bt-carousel-drag` (`false` / `0` / `off` / `no` disable). Omit the attr
 *   to use the JS flag. Drag defaults on (mouse LMB) so desktop resize-to-mobile
 *   can swipe the overflow row the same way as Oh Polly.
 *
 * Non-goals:
 * - Nav collection tiles — stay in `bt-nav.js` (index / dots / leftover columns)
 * - Horizon `slideshow-component` / `assets/slideshow.js` — PDP gallery,
 *   product-card media, resource-list, collection-links. Do not replace those
 *   here and do not use slideshow for new Bo+Tee overflow-x carousels.
 *
 * Import from section JS (`@theme/bt-scroll-carousel`). Keep the specifier in
 * the importmap in `snippets/scripts.liquid`. Do not add a global script tag.
 */

/**
 * @typedef {Object} ScrollProgress
 * @property {number} progress - Thumb-width progress 0–100
 * @property {number} maxScroll - `scrollWidth - clientWidth` (0 if not overflowing)
 * @property {boolean} atStart
 * @property {boolean} atEnd
 */

/**
 * @typedef {Object} OverflowCarouselFeatures
 * @property {boolean} [progress] - Update the progress CSS var
 * @property {boolean} [nav] - Bind prev/next and hide them at the ends
 * @property {boolean} [drag] - Mouse LMB drag-to-scroll (default on)
 */

/**
 * @typedef {Object} OverflowCarouselOptions
 * @property {HTMLElement} root - Host that owns the CSS progress variable
 * @property {HTMLElement | (() => HTMLElement | null) | null} track - Track, or getter for tabbed carousels
 * @property {Element | null} [prevButton]
 * @property {Element | null} [nextButton]
 * @property {string} [cardSelector] - First matching child used for scroll delta
 * @property {string} [progressVar] - e.g. `'--bt-cc-progress'` (required when progress is on)
 * @property {OverflowCarouselFeatures} [features] - Per-section on/off; host `data-bt-carousel-*` attrs override
 */

/**
 * @typedef {Object} OverflowCarouselBinding
 * @property {() => void} update - Re-read the active track and refresh chrome
 * @property {() => void} disconnect - Remove listeners and observers
 */

/**
 * Thumb-width scroll progress for a horizontal track.
 *
 * @param {HTMLElement} track
 * @returns {ScrollProgress}
 */
export function getScrollProgress(track) {
  const maxScroll = track.scrollWidth - track.clientWidth;
  if (maxScroll <= 1) {
    return { progress: 100, maxScroll: 0, atStart: true, atEnd: true };
  }

  const thumbPercent = (track.clientWidth / track.scrollWidth) * 100;
  const progress = thumbPercent + (track.scrollLeft / maxScroll) * (100 - thumbPercent);
  return {
    progress: Math.round(progress),
    maxScroll,
    atStart: track.scrollLeft <= 2,
    atEnd: track.scrollLeft >= maxScroll - 2,
  };
}

/**
 * Scroll the track by one card width plus gap.
 *
 * @param {HTMLElement} track
 * @param {number} direction - `1` = next, `-1` = previous
 * @param {string} [cardSelector]
 */
export function scrollByCard(track, direction, cardSelector) {
  const card = cardSelector ? track.querySelector(cardSelector) : null;
  const styles = getComputedStyle(track);
  const gap = Number.parseFloat(styles.columnGap || styles.gap || '1') || 1;
  const delta = card instanceof HTMLElement ? card.getBoundingClientRect().width + gap : track.clientWidth;
  track.scrollBy({
    left: direction * delta,
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });
}

/**
 * @param {string | undefined} raw
 * @returns {boolean | undefined}
 */
function parseFeatureAttr(raw) {
  if (raw == null || raw === '') return undefined;
  return !['false', '0', 'off', 'no'].includes(String(raw).trim().toLowerCase());
}

/**
 * Host `data-bt-carousel-*` wins when present, then the JS flag, then fallback.
 *
 * @param {HTMLElement} root
 * @param {'btCarouselProgress' | 'btCarouselNav' | 'btCarouselDrag'} datasetKey
 * @param {boolean | undefined} explicit
 * @param {boolean} fallback
 * @returns {boolean}
 */
function resolveFeature(root, datasetKey, explicit, fallback) {
  const fromAttr = parseFeatureAttr(root.dataset[datasetKey]);
  if (typeof fromAttr === 'boolean') return fromAttr;
  if (typeof explicit === 'boolean') return explicit;
  return fallback;
}

/** Pixels of pointer travel before a press becomes a drag (keeps clicks working). */
const DRAG_THRESHOLD_PX = 5;

/**
 * Bind progress chrome, resize, optional prev/next, and mouse drag to an overflow-x track.
 *
 * @param {OverflowCarouselOptions} options
 * @returns {OverflowCarouselBinding}
 */
export function bindOverflowCarousel(options) {
  const { root, prevButton, nextButton, cardSelector, progressVar, features } = options;
  const progressEnabled = resolveFeature(
    root,
    'btCarouselProgress',
    features?.progress,
    Boolean(progressVar)
  );
  const navEnabled = resolveFeature(
    root,
    'btCarouselNav',
    features?.nav,
    Boolean(prevButton || nextButton)
  );
  const dragEnabled = resolveFeature(root, 'btCarouselDrag', features?.drag, true);

  /**
   * @returns {HTMLElement | null}
   */
  const resolveTrack = () => {
    const track = typeof options.track === 'function' ? options.track() : options.track;
    return track instanceof HTMLElement ? track : null;
  };

  const applyChrome = () => {
    const track = resolveTrack();
    if (!track) {
      if (progressEnabled && progressVar) root.style.setProperty(progressVar, '100%');
      if (navEnabled) {
        if (prevButton instanceof HTMLElement) prevButton.hidden = true;
        if (nextButton instanceof HTMLElement) nextButton.hidden = true;
      }
      return;
    }

    const state = getScrollProgress(track);
    if (progressEnabled && progressVar) {
      root.style.setProperty(progressVar, `${state.progress}%`);
    }
    if (navEnabled) {
      if (prevButton instanceof HTMLElement) prevButton.hidden = state.atStart;
      if (nextButton instanceof HTMLElement) nextButton.hidden = state.atEnd;
    }
  };

  const onScroll = () => {
    applyChrome();
  };

  /**
   * @param {MouseEvent} event
   * @param {number} direction
   */
  const onNav = (event, direction) => {
    event.preventDefault();
    const track = resolveTrack();
    if (track) scrollByCard(track, direction, cardSelector);
  };

  /** @param {MouseEvent} event */
  const onPrev = (event) => onNav(event, -1);
  /** @param {MouseEvent} event */
  const onNext = (event) => onNav(event, 1);

  if (!progressEnabled && !navEnabled && !dragEnabled) {
    if (prevButton instanceof HTMLElement) prevButton.hidden = true;
    if (nextButton instanceof HTMLElement) nextButton.hidden = true;
    return {
      update() {},
      disconnect() {},
    };
  }

  if (!navEnabled) {
    if (prevButton instanceof HTMLElement) prevButton.hidden = true;
    if (nextButton instanceof HTMLElement) nextButton.hidden = true;
  }

  const controller = new AbortController();
  const { signal } = controller;

  /**
   * Restore snap / cursor after a drag so the track can settle on a card.
   *
   * @param {HTMLElement} track
   */
  const clearDragStyles = (track) => {
    track.style.removeProperty('scroll-snap-type');
    track.style.removeProperty('cursor');
    track.style.removeProperty('user-select');
  };

  /**
   * Mouse LMB drag on the active track. Touch keeps native overflow-x swipe.
   * Capture-phase mousedown stops Horizon card-gallery from stealing the gesture.
   *
   * @param {PointerEvent} event
   */
  const onPointerDown = (event) => {
    if (!dragEnabled) return;
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    const track = resolveTrack();
    if (!track || getScrollProgress(track).maxScroll <= 0) return;
    if (!(event.target instanceof Node) || !track.contains(event.target)) return;
    if (event.target instanceof Element && event.target.closest('input, textarea, select')) return;

    const startX = event.clientX;
    const startScroll = track.scrollLeft;
    let dragged = false;
    const drag = new AbortController();

    const onMouseDownCapture = (/** @type {MouseEvent} */ mouseEvent) => {
      mouseEvent.stopPropagation();
    };
    track.addEventListener('mousedown', onMouseDownCapture, { capture: true, once: true, signal: drag.signal });

    const onPointerMove = (/** @type {PointerEvent} */ moveEvent) => {
      const dx = moveEvent.clientX - startX;
      if (!dragged) {
        if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
        dragged = true;
        try {
          root.setPointerCapture(moveEvent.pointerId);
        } catch {
          // Capture can fail if the pointer already ended.
        }
        track.style.scrollSnapType = 'none';
        track.style.cursor = 'grabbing';
        track.style.userSelect = 'none';
      }
      track.scrollLeft = startScroll - (moveEvent.clientX - startX);
    };

    const endDrag = () => {
      drag.abort();
      clearDragStyles(track);
      if (dragged) {
        root.addEventListener(
          'click',
          (clickEvent) => {
            clickEvent.preventDefault();
            clickEvent.stopPropagation();
          },
          { capture: true, once: true }
        );
      }
    };

    root.addEventListener('pointermove', onPointerMove, { signal: drag.signal });
    root.addEventListener('pointerup', endDrag, { signal: drag.signal });
    root.addEventListener('pointercancel', endDrag, { signal: drag.signal });
  };

  /**
   * @param {DragEvent} event
   */
  const onDragStart = (event) => {
    const track = resolveTrack();
    if (!track || !(event.target instanceof Node) || !track.contains(event.target)) return;
    event.preventDefault();
  };

  root.addEventListener('scroll', onScroll, { passive: true, capture: true, signal });
  if (navEnabled) {
    prevButton?.addEventListener('click', onPrev, { signal });
    nextButton?.addEventListener('click', onNext, { signal });
  }
  if (dragEnabled) {
    root.addEventListener('pointerdown', onPointerDown, { signal });
    root.addEventListener('dragstart', onDragStart, { signal });
  }

  const resizeObserver = new ResizeObserver(() => applyChrome());
  resizeObserver.observe(root);
  const initialTrack = resolveTrack();
  if (initialTrack) resizeObserver.observe(initialTrack);

  applyChrome();

  return {
    update() {
      const track = resolveTrack();
      if (track) resizeObserver.observe(track);
      applyChrome();
    },
    disconnect() {
      controller.abort();
      const track = resolveTrack();
      if (track) clearDragStyles(track);
      resizeObserver.disconnect();
    },
  };
}
